'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkflowStore } from '../../stores/workflowStore';
import { NODE_META } from './nodeTypes';

import { FilterConfig }      from './configs/FilterConfig';
import { JoinConfig }        from './configs/JoinConfig';
import { AggregateConfig }   from './configs/AggregateConfig';
import { SortConfig }        from './configs/SortConfig';
import { LoadExcelConfig }   from './configs/LoadExcelConfig';
import { LoadCSVConfig }     from './configs/LoadCSVConfig';
import { LoadAPIConfig }     from './configs/LoadAPIConfig';
import { ComputeConfig }     from './configs/ComputeConfig';
import { DedupConfig }       from './configs/DedupConfig';
import { GroupByConfig }     from './configs/GroupByConfig';
import { AppendConfig }      from './configs/AppendConfig';
import { DiffConfig }        from './configs/DiffConfig';
import { ExportExcelConfig } from './configs/ExportExcelConfig';
import { ExportCSVConfig }   from './configs/ExportCSVConfig';
import { X, Sliders, Circle, PencilSimple, Check } from '@phosphor-icons/react';

type ConfigComp = React.ComponentType<{ nodeId: string; config: Record<string, unknown> }>;

const configComponents: Record<string, ConfigComp> = {
  filter:       FilterConfig      as ConfigComp,
  join:         JoinConfig        as ConfigComp,
  aggregate:    AggregateConfig   as ConfigComp,
  sort:         SortConfig        as ConfigComp,
  load_excel:   LoadExcelConfig   as ConfigComp,
  load_csv:     LoadCSVConfig     as ConfigComp,
  load_api:     LoadAPIConfig     as ConfigComp,
  compute:      ComputeConfig     as ConfigComp,
  dedup:        DedupConfig       as ConfigComp,
  group_by:     GroupByConfig     as ConfigComp,
  append:       AppendConfig      as ConfigComp,
  diff:         DiffConfig        as ConfigComp,
  export_excel: ExportExcelConfig as ConfigComp,
  export_csv:   ExportCSVConfig   as ConfigComp,
};

function EmptyState() {
  return (
    <motion.div
      className="flex flex-col items-center justify-center h-full p-8 text-center"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
    >
      <motion.div
        className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mb-4"
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Sliders className="w-8 h-8 text-zinc-300" />
      </motion.div>
      <h3 className="text-sm font-semibold text-zinc-900 mb-1">选择节点</h3>
      <p className="text-xs text-zinc-500 max-w-[24ch]">点击画布中的节点以编辑其属性配置</p>
    </motion.div>
  );
}

interface PropertyPanelProps {
  className?: string;
}

export function PropertyPanel({ className = '' }: PropertyPanelProps) {
  const { selectedNode, setSelectedNode, updateNode } = useWorkflowStore();
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelValue, setLabelValue]     = useState('');
  const labelInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedNode) {
      setLabelValue(selectedNode.data.label || selectedNode.type);
      setEditingLabel(false);
    }
  }, [selectedNode?.id]);

  useEffect(() => {
    if (editingLabel) labelInputRef.current?.focus();
  }, [editingLabel]);

  const handleLabelSave = () => {
    if (selectedNode && labelValue.trim()) {
      updateNode(selectedNode.id, {
        data: { ...selectedNode.data, label: labelValue.trim() },
      });
    }
    setEditingLabel(false);
  };

  if (!selectedNode) {
    return (
      <motion.div
        className={`bg-white border-l border-zinc-200 h-full overflow-hidden ${className}`}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      >
        <EmptyState />
      </motion.div>
    );
  }

  const meta = NODE_META[selectedNode.type];
  const Icon = meta?.icon ?? Sliders;
  const ConfigComponent = configComponents[selectedNode.type];

  return (
    <motion.div
      className={`bg-white border-l border-zinc-200 h-full overflow-hidden flex flex-col ${className}`}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100">
        <div className="flex items-center gap-3">
          {/* Node icon with category color */}
          <div className={`w-9 h-9 rounded-xl ${meta?.bg ?? 'bg-zinc-100'} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${meta?.color ?? 'text-zinc-500'}`} weight="fill" />
          </div>

          {/* Label + type */}
          <div className="flex-1 min-w-0">
            {editingLabel ? (
              <div className="flex items-center gap-1">
                <input
                  ref={labelInputRef}
                  value={labelValue}
                  onChange={(e) => setLabelValue(e.target.value)}
                  onBlur={handleLabelSave}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLabelSave(); if (e.key === 'Escape') setEditingLabel(false); }}
                  className="flex-1 text-sm font-semibold text-zinc-900 bg-zinc-100 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 min-w-0"
                />
                <button onClick={handleLabelSave} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors flex-shrink-0">
                  <Check className="w-3.5 h-3.5" weight="bold" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 group">
                <h2 className="text-sm font-semibold text-zinc-900 truncate">{selectedNode.data.label || meta?.label || selectedNode.type}</h2>
                <button
                  onClick={() => setEditingLabel(true)}
                  className="p-0.5 text-zinc-300 hover:text-zinc-600 opacity-0 group-hover:opacity-100 transition-all rounded flex-shrink-0"
                  title="编辑名称"
                >
                  <PencilSimple className="w-3 h-3" />
                </button>
              </div>
            )}
            <p className="text-xs text-zinc-400">{meta?.label ?? selectedNode.type} · <span className="font-mono">{selectedNode.id}</span></p>
          </div>

          <motion.button
            onClick={() => setSelectedNode(null)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      {/* Config Content */}
      <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedNode.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ type: 'spring', stiffness: 100, damping: 20 }}
          >
            {ConfigComponent ? (
              <ConfigComponent nodeId={selectedNode.id} config={selectedNode.data.config.params as Record<string, unknown>} />
            ) : (
              <div className="text-sm text-zinc-500 bg-zinc-50 rounded-xl p-4 border border-zinc-100">
                <div className="flex items-center gap-2 text-zinc-400 mb-2">
                  <Circle className="w-4 h-4" />
                  <span className="font-medium">无需配置</span>
                </div>
                <p className="text-xs">此节点类型暂无配置选项</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      {/* <div className="p-4 border-t border-zinc-100 bg-zinc-50/50">
        <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
          <Button
            variant="danger"
            className="w-full"
            leftIcon={<Trash className="w-4 h-4" />}
            onClick={() => { removeNode(selectedNode.id); setSelectedNode(null); }}
          >
            删除节点
          </Button>
        </motion.div>
      </div> */}
    </motion.div>
  );
}
